import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Fade,
  useTheme,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { productService } from "../services/products";
import { cmsService, CMSHeroSection } from "../services/cms";
import { Product } from "../types";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { PageTitle, FeaturedEquipmentSwiper, ExploreSection, RecentlyAddedOffers, BrandSpotlight, OurBrands, OurCustomers, OurProcess, ProductsHero } from "../components";
import PublicSellerReviews from "../components/PublicSellerReviews";

const ITEMS_PER_PAGE = 12;

const Products: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, items } = useCart();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // For featured equipment
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addedToCart, setAddedToCart] = useState<string[]>([]);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Filter states
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [condition, setCondition] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [categories, setCategories] = useState<string[]>([]);

  // Hero swiper state
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Hero sections from CMS
  const [heroSections, setHeroSections] = useState<CMSHeroSection[]>([]);
  const [loadingHero, setLoadingHero] = useState(true);

  // Fallback hero slides (used if CMS has no data)
  const FALLBACK_HERO_SLIDES: CMSHeroSection[] = [
    {
      background_image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?q=80&w=2000&auto=format&fit=crop",
      title: t('hero.title'),
      subtitle: t('hero.subtitle'),
      cta_text: t('hero.exploreProducts'),
      cta_link: "/category/all",
      gradient_start: "#00d4ff",
      gradient_end: "#0099cc",
      is_active: true,
      display_order: 0,
    },
    {
      background_image: "https://images.unsplash.com/photo-1581594549595-35f6c54d7754?q=80&w=2000&auto=format&fit=crop",
      title: t('hero.slide2Title'),
      subtitle: t('hero.slide2Subtitle'),
      cta_text: t('hero.exploreProducts'),
      cta_link: "/category/all",
      gradient_start: "#00ff88",
      gradient_end: "#00cc6f",
      is_active: true,
      display_order: 1,
    },
    {
      background_image: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?q=80&w=2000&auto=format&fit=crop",
      title: t('hero.slide3Title'),
      subtitle: t('hero.slide3Subtitle'),
      cta_text: t('hero.exploreProducts'),
      cta_link: "/category/all",
      gradient_start: "#ff0080",
      gradient_end: "#cc0066",
      is_active: true,
      display_order: 2,
    },
    {
      background_image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2000&auto=format&fit=crop",
      title: t('hero.slide4Title'),
      subtitle: t('hero.slide4Subtitle'),
      cta_text: t('hero.exploreProducts'),
      cta_link: "/category/all",
      gradient_start: "#ffaa00",
      gradient_end: "#cc8800",
      is_active: true,
      display_order: 3,
    },
  ];

  // Use CMS hero sections or fallback
  const HERO_SLIDES = heroSections.length > 0 ? heroSections : FALLBACK_HERO_SLIDES;

  // Fetch Hero Sections from CMS
  useEffect(() => {
    fetchHeroSections();

    // Subscribe to real-time updates
    const subscription = cmsService.subscribeToChanges('cms_hero_sections', () => {
      fetchHeroSections();
    });

    return () => {
      cmsService.unsubscribe(subscription);
    };
  }, []);

  const fetchHeroSections = async () => {
    try {
      setLoadingHero(true);
      const { data, error } = await cmsService.getHeroSections(true); // Only get active sections
      if (!error && data && data.length > 0) {
        setHeroSections(data);
        // Reset to first slide when data changes
        setActiveSlide(0);
      }
    } catch (error) {
      console.error('Error fetching hero sections:', error);
    } finally {
      setLoadingHero(false);
    }
  };

  const nextSlide = () => {
    if (!isTransitioning && HERO_SLIDES.length > 0) {
      setIsTransitioning(true);
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
      setTimeout(() => setIsTransitioning(false), 800);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning && HERO_SLIDES.length > 0) {
      setIsTransitioning(true);
      setActiveSlide(
        (prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
      );
      setTimeout(() => setIsTransitioning(false), 800);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isTransitioning]);

  // SEO: set title and meta description
  useEffect(() => {
    document.title = "Medical Equipment | Zetta Med Products";
    const desc =
      "Browse premium medical equipment: imaging, surgical, monitoring and lab devices.";
    
    // Description
    let meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", desc);
    } else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }

    // Open Graph Title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", "Medical Equipment | Zetta Med Products");
    } else {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      m.content = "Medical Equipment | Zetta Med Products";
      document.head.appendChild(m);
    }

    // Open Graph Description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute("content", desc);
    } else {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:description");
      m.content = desc;
      document.head.appendChild(m);
    }
    
    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.href);
  }, []);

  // Preload hero images
  useEffect(() => {
    HERO_SLIDES.forEach((slide) => {
      if (slide.background_image) {
        const img = new Image();
        img.src = slide.background_image;
      }
    });
  }, [HERO_SLIDES]);

  // Autoplay
  useEffect(() => {
    if (!isPaused && !isTransitioning && HERO_SLIDES.length > 1) {
      const id = window.setTimeout(() => nextSlide(), 5000);
      return () => window.clearTimeout(id);
    }
  }, [activeSlide, isPaused, isTransitioning, HERO_SLIDES.length]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [search, category, condition, priceRange, page]);

  const fetchCategories = async () => {
    const { data, error } = await productService.getCategories();
    if (!error && data) {
      setCategories(data);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError("");

    const filters: any = {
      search: search || undefined,
      category: category || undefined,
      condition: condition || undefined,
    };

    // Parse price range
    if (priceRange) {
      const [min, max] = priceRange.split("-").map((p) => parseInt(p));
      if (min) filters.minPrice = min;
      if (max) filters.maxPrice = max;
    }

    try {
      const { data, error: fetchError } = await productService.getProducts(filters);
      if (fetchError) {
        setError(t('products.fetchError', { defaultValue: 'Failed to load products. Please try again.' }));
        console.error("Error fetching products:", fetchError);
        setAllProducts([]);
        setProducts([]);
        setTotalPages(0);
      } else if (!data || data.length === 0) {
        // No products in database - show empty state
        setAllProducts([]);
        setProducts([]);
        setTotalPages(0);
      } else {
        setAllProducts(data); // Store all products for featured equipment
        // Client-side pagination
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setProducts(data.slice(startIndex, endIndex));
        setTotalPages(Math.ceil(data.length / ITEMS_PER_PAGE));
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(t('products.fetchError', { defaultValue: 'Failed to load products. Please try again.' }));
      setAllProducts([]);
      setProducts([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Keep a ref to the latest fetchProducts so the subscription always calls the current one
  const fetchProductsRef = useRef(fetchProducts);
  fetchProductsRef.current = fetchProducts;

  // Subscribe to real-time product changes so carousels update reactively
  useEffect(() => {
    const subscription = cmsService.subscribeToChanges('products', () => {
      fetchProductsRef.current();
    });

    return () => {
      cmsService.unsubscribe(subscription);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case "category":
        setCategory(value);
        break;
      case "condition":
        setCondition(value);
        break;
      case "priceRange":
        setPriceRange(value);
        break;
    }
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const handleAddToCart = (product: Product) => {
    if (!user) {
      navigate("/login");
      return;
    }
    addToCart(product);
    setAddedToCart((prev) => [...prev, product.id]);
    setTimeout(() => {
      setAddedToCart((prev) => prev.filter((id) => id !== product.id));
    }, 2000);
  };

  const isInCart = (productId: string) => {
    return items.some((item) => item.product.id === productId);
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case "excellent":
        return "#00ff88";
      case "good":
        return "#00d4ff";
      default:
        return "#ffaa00";
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 4, md: 6 } }}>
      <Fade in timeout={800}>
        <Box>
          <ProductsHero
            heroSlides={HERO_SLIDES}
            activeSlide={activeSlide}
            isPaused={isPaused}
            isTransitioning={isTransitioning}
            isDark={isDark}
            backgroundDefault={theme.palette.background.default}
            backgroundPaper={theme.palette.background.paper}
            textPrimary={theme.palette.text.primary}
            onPauseChange={setIsPaused}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onPrevSlide={prevSlide}
            onNextSlide={nextSlide}
            onSlideChange={(index) => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setActiveSlide(index);
                setTimeout(() => setIsTransitioning(false), 800);
              }
            }}
            exploreProductsLabel={t('hero.exploreProducts')}
          />

         
          {/* Featured Equipment Section */}
          {allProducts.length > 0 && (
            <Box sx={{ py: { xs: 2, md: 3 } }}>
              <PageTitle
                text={t('featuredEquipment.title')}
                align="left"
                size="medium"
              />
              <FeaturedEquipmentSwiper
                products={allProducts.slice(0, 14)} // Show first 14 products
                onAddToCart={handleAddToCart}
              />
            </Box>
          )}

          {/* Explore Section */}
          <Box sx={{ py: { xs: 2, md: 0 } }}>
            <ExploreSection />
          </Box>
         
          {/* Recently Added Offers Section */}
          <Box sx={{ py: { xs: 2, md:1} }}>
            <PageTitle
              text={t('recentOffers.title')}
              align="left"
              size="medium"
            />
            <RecentlyAddedOffers onAddToCart={handleAddToCart} />
          </Box>
 
          {/* Brand Spotlight Section */}
          {allProducts.length > 0 && (
            <Box sx={{ py: { xs: 2, md: 3 } }}>
              <PageTitle
                text={t('brandSpotlight.title')}
                align="left"
                size="medium"
              />
              <BrandSpotlight
                products={allProducts.filter(p =>
                  // Filter for products that could be from Thermo Fisher (based on category/type)
                  p.category === 'Laboratory Equipment' ||
                  p.category === 'Diagnostic Equipment' ||
                  p.title.toLowerCase().includes('analyzer') ||
                  p.title.toLowerCase().includes('centrifuge')
                ).slice(0, 10)}
                onAddToCart={handleAddToCart}
              />
            </Box>
          )}
        
          {/* Our Process Section */}
          <Box sx={{ py: { xs: 2, md: 1 } }}>
            <OurProcess />
          </Box>
          
          {/* Our Brands Section */}
          <Box sx={{ py: { xs: 2, md: 1 } }}>
            <OurBrands />
          </Box>
         
          {/* Our customer Section */}
          <Box sx={{ py: { xs: 2, md: 1 } }}>
            <OurCustomers />
          </Box>
         
          {/* Seller Reviews Section */}
          <Box sx={{ py: { xs: 4, md: 6 } }}>
            <PublicSellerReviews />
          </Box>
       
          
        </Box>
      </Fade>
      </Container>
    </Box>
  );
};

export default Products;
